import Sockette from "sockette";
import { Message } from "./message";

export type PushClientConfig = {
	url: string,
	authToken: string
	maxAttempts : number,
	timeout : number,
	pingInterval: number,
	onOpen: () => void,
	onClose: () => void,
	onMessage: (message: any) => void
}

export class Connection {
	private readonly _url: string;
	private readonly _sockette: Sockette;
	private readonly _onOpen: () => void;
	private readonly _onClose: () => void;
	private readonly _onMessage: (message: any) => void;
	private _authToken: string;
	private _resumeToken: any;
	private _pingInterval: number;
	private _pingHandle: number | NodeJS.Timeout;
	private _firstOpen = true;

	constructor(config: PushClientConfig) {
		this._url = config.url;
		this._authToken = config.authToken;
		this._pingInterval = config.pingInterval || (8 * 60000);
		this._onOpen = config.onOpen;
		this._onClose = config.onClose;
		this._onMessage = config.onMessage;

		this._sockette = new Sockette(
			`${this._url}?authorization=${this._authToken}`, 
			{
				timeout: config.timeout || 5e3,
				maxAttempts: config.maxAttempts || 10,
				onopen: this.Socket_onOpen.bind(this),
				onclose: this.Socket_onClose.bind(this),
				onreconnect: this.Socket_onReconnect.bind(this),
				onmessage: this.Socket_onMessage.bind(this),
				onmaximum: this.Socket_onMaximum.bind(this),
				onerror: this.Socket_onError.bind(this)
			}
		);
	}

	public open() {
		this._firstOpen = true;
		this._sockette.open();
	}

	public close() {
		this.stopPing();
		this._sockette.close();
	}

	private pull() {
		this._sockette.json({ action: 'pull', token: this._resumeToken });
	}

	private ping(firstPing?: boolean) {
		this._sockette.json({ action: 'ping', firstPing: !!firstPing });
	}

	private startPing() {
		this.resetPing();

		if (!this._resumeToken) {
			this.ping(true);
		}
	}

	private resetPing() {
		this.stopPing();
		this._pingHandle = setInterval(
			this.ping.bind(this),
			this._resumeToken ? this._pingInterval : 30000
		);
	}

	private stopPing() {
		if (this._pingHandle) {
			clearInterval(this._pingHandle);
		}
	}

	private Socket_onOpen(ev: Event) {
		this.startPing();

		if (this._onOpen) {
			this._onOpen();
		}

		if (!this._firstOpen) {
			this.pull();
		} else {
			this._firstOpen = false;
		}
	}

	private Socket_onClose(ev: CloseEvent) {
		this.stopPing();

		if (ev.code === 1000 && this._onClose) {
			this._onClose();
		}
	}

	private Socket_onReconnect(ev: Event | CloseEvent) {
		this.startPing();
	}

	private Socket_onMessage(ev: MessageEvent) {
		const message: Message = JSON.parse(ev.data);

		switch (message.action) {
			case 'pull':
				this._resumeToken = message.token;

				for (const notification of message.notifications) {
					this._onMessage(notification.message);
				}

				break;
			case 'push':
				if (this._onMessage) {
					this._resumeToken = message.token;
					this._onMessage(message.notification.message);
				}

				break;
			case 'ping': 
				if (!this._resumeToken && message.token) {
					this._resumeToken = message.token;
				}

				break;
			default:
				break;
		}

		this.resetPing();
	}

	private Socket_onMaximum(ev: CloseEvent) {
		this.stopPing();

		this._resumeToken = false;

		if (this._onClose) {
			this._onClose();
		}
	}

	private Socket_onError(ev: Event) {
	}
}